import { useState, FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, CheckCircle } from "lucide-react";
import axios from "axios";
import { supabase } from "../../lib/supabaseClient";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";

const NAVY = "#2a2420";
const ORANGE = "#607840";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_PATTERN = /^\+?[0-9\s().-]{8,20}$/;

const inputStyle = {
  border: `1.5px solid ${NAVY}22`,
  color: NAVY,
  backgroundColor: "#f8f3ea",
};

function FloatingField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      <span
        className="absolute -top-2 left-3 z-10 px-1 text-xs font-semibold"
        style={{ color: ORANGE, backgroundColor: "#fefcf8" }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

async function checkAvailability(field: "email" | "phone", value: string): Promise<boolean> {
  if (!value.trim()) return false;
  try {
    const { data } = await axios.get(`${API_BASE_URL}/check-availability`, {
      params: { [field]: value.trim() },
    });
    return field === "email" ? data.emailTaken : data.phoneTaken;
  } catch {
    return false;
  }
}

function isValidEmail(value: string) {
  return EMAIL_PATTERN.test(value.trim().toLowerCase());
}

function phoneDigitCount(value: string) {
  return value.replace(/\D/g, "").length;
}

function isValidPhone(value: string) {
  const trimmed = value.trim();
  const digits = phoneDigitCount(trimmed);
  return PHONE_PATTERN.test(trimmed) && digits >= 8 && digits <= 15;
}

function normalizePhone(value: string) {
  return value.trim().replace(/[^\d+]/g, "");
}

export default function SignUpPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailWarning, setEmailWarning] = useState<string | null>(null);
  const [phoneWarning, setPhoneWarning] = useState<string | null>(null);

  async function handleEmailBlur() {
    const cleanEmail = email.trim().toLowerCase();
    setEmail(cleanEmail);
    if (!cleanEmail) return;
    if (!isValidEmail(cleanEmail)) {
      setEmailWarning("Enter a valid email address.");
      return;
    }

    const taken = await checkAvailability("email", cleanEmail);
    setEmailWarning(taken ? "An account with this email already exists. Please sign in instead." : null);
  }

  async function handlePhoneBlur() {
    const cleanPhone = phone.trim();
    setPhone(cleanPhone);
    if (!cleanPhone) return;
    if (!isValidPhone(cleanPhone)) {
      setPhoneWarning("Enter a valid phone number.");
      return;
    }

    const taken = await checkAvailability("phone", normalizePhone(cleanPhone));
    setPhoneWarning(taken ? "This phone number is already linked to another account." : null);
  }

  function inputProps(onFocus?: () => void, onBlur?: () => void) {
    return {
      className:
        "w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-all",
      style: inputStyle,
      onFocus: (e: React.FocusEvent<any>) => {
        e.currentTarget.style.border = `1.5px solid ${ORANGE}`;
        onFocus?.();
      },
      onBlur: (e: React.FocusEvent<any>) => {
        e.currentTarget.style.border = `1.5px solid ${NAVY}22`;
        onBlur?.();
      },
    };
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const cleanEmail = email.trim().toLowerCase();
    const cleanPhone = normalizePhone(phone);

    if (!isValidEmail(cleanEmail)) {
      setEmailWarning("Enter a valid email address.");
      return;
    }

    if (!isValidPhone(phone)) {
      setPhoneWarning("Enter a valid phone number.");
      return;
    }

    setLoading(true);

    const { data, error: signUpError } = await supabase.auth.signUp({
      email: cleanEmail,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/confirm`,
        data: {
          name: name.trim(),
          phone: cleanPhone,
        },
      },
    });

    if (signUpError) {
      setLoading(false);
      setError(signUpError.message);
      return;
    }

    // If a session is available immediately (email confirmation disabled),
    // update the profile with the extra registration details now.
    if (data.session && data.user) {
      await supabase
        .from("profiles")
        .upsert({
          id: data.user.id,
          email: cleanEmail,
          name: name.trim(),
          phone: cleanPhone,
          updated_at: new Date().toISOString(),
        });

      setLoading(false);
      navigate("/businesses", { replace: true });
      return;
    }

    // Email confirmation required — show the holding screen.
    setLoading(false);
    setEmailSent(true);
  }

  if (emailSent) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: "#fefcf8" }}
      >
        <div className="w-full max-w-sm text-center">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: "rgba(16,185,129,0.1)" }}
          >
            <CheckCircle size={28} style={{ color: "#059669" }} />
          </div>
          <h1 className="text-xl font-extrabold mb-2" style={{ color: NAVY }}>
            Check your email
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: NAVY, opacity: 0.55 }}>
            We sent a confirmation link to <strong>{email}</strong>. Click it to
            activate your account and continue setup.
          </p>
          <Link
            to="/login"
            className="inline-block mt-6 text-sm font-semibold"
            style={{ color: ORANGE }}
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-screen overflow-hidden relative">
      {/* Left: quote artwork */}
      <div
        className="hidden lg:block"
        style={{
          flex: "0 0 60%",
          backgroundImage: "url(/onboard.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#f5f7f2",
        }}
      />

      {/* Right: sign up panel */}
      <div
        className="flex flex-col justify-center px-10 py-12 w-full lg:w-auto overflow-y-auto"
        style={{
          flex: "0 0 40%",
          backgroundColor: "#fefcf8",
          borderLeft: "1px solid rgba(80,55,30,0.08)",
        }}
      >
        <div className="w-full max-w-sm mx-auto">
          <h1 className="mb-8 text-center text-3xl font-extrabold" style={{ color: NAVY }}>
            Sign up for Bill Lens
          </h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            <FloatingField label="Name">
              <input
                type="text"
                required
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder=""
                {...inputProps()}
              />
            </FloatingField>

            <FloatingField label="Email">
              <input
                type="email"
                required
                autoComplete="email"
                autoCapitalize="none"
                maxLength={254}
                pattern="[^\s@]+@[^\s@]+\.[^\s@]{2,}"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailWarning(null); }}
                placeholder=""
                {...inputProps(undefined, handleEmailBlur)}
              />
              {emailWarning && (
                <p className="mt-1.5 text-xs px-2 py-1.5 rounded-lg" style={{ color: "#92400e", backgroundColor: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)" }}>
                  {emailWarning}
                </p>
              )}
            </FloatingField>

            <FloatingField label="Phone number">
              <input
                type="tel"
                required
                autoComplete="tel"
                inputMode="tel"
                minLength={8}
                maxLength={20}
                pattern="\+?[0-9\s().-]{8,20}"
                title="Use 8 to 15 digits. Spaces, hyphens, parentheses, and a leading + are allowed."
                value={phone}
                onChange={(e) => { setPhone(e.target.value); setPhoneWarning(null); }}
                placeholder=""
                {...inputProps(undefined, handlePhoneBlur)}
              />
              {phoneWarning && (
                <p className="mt-1.5 text-xs px-2 py-1.5 rounded-lg" style={{ color: "#92400e", backgroundColor: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)" }}>
                  {phoneWarning}
                </p>
              )}
            </FloatingField>

            <FloatingField label="Password">
              <input
                type="password"
                required
                autoComplete="new-password"
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder=""
                {...inputProps()}
              />
            </FloatingField>

            {error && (
              <p
                className="text-xs px-3 py-2 rounded-lg"
                style={{
                  color: "#991b1b",
                  backgroundColor: "rgba(220,38,38,0.06)",
                  border: "1px solid rgba(220,38,38,0.2)",
                }}
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{
                backgroundColor: ORANGE,
                boxShadow: "0 3px 12px rgba(96,120,64,0.3)",
              }}
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {loading ? "Creating account..." : "Create account"}
            </button>
          </form>

          <p className="text-center text-sm mt-6" style={{ color: NAVY, opacity: 0.55 }}>
            Already have an account?{" "}
            <Link to="/login" className="font-semibold" style={{ color: ORANGE }}>
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
